<template>
  <div class="reports-sales-page">
    <ReportsPageShell
      :title="currentHeaderTitle"
      :subtitle="currentHeaderSubtitle"
      :actions="headerActions"
      @action="onHeaderAction"
    >
      <template #income-tabs>
        <ReportsPrimaryTabs
          :tabs="reportTabs"
          :active-tab="activeReportTab"
          @select="handleReportTabSelect"
        />
      </template>

      <template #metrics>
        <ReportsKpiSection
          :class="{ 'reports-kpi-section--profitability': isSalesProfitabilityTab }"
          :cards="kpiCards"
          :desktop-columns="isSalesProfitabilityTab ? 5 : 4"
          :laptop-columns="isSalesProfitabilityTab ? 3 : 4"
          :tablet-columns="isSalesProfitabilityTab ? 3 : 2"
        />
      </template>

      <template #toolbar>
        <ReportsFilterToolbar
          report-key="sales"
          :report-tab="activeReportTab"
          @filters-change="handleToolbarFiltersChange"
          @apply="handleApplyFilters"
        />
      </template>

      <template #chart>
        <ReportsChartSection
          :mode="chartMode"
          :points="chartPoints"
          :series-data="chartSeriesData"
          :categories="chartCategories"
          :allow-visibility-toggle="true"
          visibility-storage-key="reports.sales.chart.hidden"
          :variant="isSalesProfitabilityTab ? salesProfitabilityChartMetric : ''"
          :variant-options="isSalesProfitabilityTab ? salesProfitabilityChartVariantOptions : []"
          @update:mode="chartMode = $event"
          @update:variant="handleSalesProfitabilityChartVariantChange"
        />
      </template>

      <template #table>
        <ReportsSalesProfitabilityTable
          v-if="isSalesProfitabilityTab"
          :rows="profitabilityTableRows"
          :summary="profitabilityTableSummary"
          :pivot-columns="profitabilityTablePivotColumns"
          :filters="salesProfitabilityFilters"
          sticky-header
          :sort-direction="activeSortDirection"
          @metric-cell-click="handleProfitabilityMetricCellClick"
        />
        <ReportsTable
          v-else
          :table="tableData"
          sticky-header
          :sort-direction="activeSortDirection"
          @period-cell-click="handlePeriodCellClick"
        />
      </template>
    </ReportsPageShell>

    <ReportsDetailModal
      :show="detailModal.show"
      :loading="detailModal.loading"
      :title="detailModal.title"
      :context-line="detailModal.contextLine"
      :subtitle="detailModal.subtitle"
      :columns="detailModal.columns"
      :rows="detailModal.rows"
      :grand-total="detailModal.grandTotal"
      @close="closeDetailModal"
    />
  </div>
</template>

<script>
import ReportsPageShell from '@/components/reports/ReportsPageShell.vue';
import ReportsPrimaryTabs from '@/components/reports/ReportsPrimaryTabs.vue';
import ReportsKpiSection from '@/components/reports/ReportsKpiSection.vue';
import ReportsFilterToolbar from '@/components/reports/ReportsFilterToolbar.vue';
import ReportsChartSection from '@/components/reports/ReportsChartSection.vue';
import ReportsTable from '@/components/reports/ReportsTable.vue';
import ReportsDetailModal from '@/components/reports/ReportsDetailModal.vue';
import ReportsSalesProfitabilityTable from '@/components/reports/sales/ReportsSalesProfitabilityTable.vue';
import reportsSalesService from '@/services/reportsSales';
import { extractPayload } from '@/utils/dataNormalization';
import { mapReportsChartPoints } from '@/composables/reports/general/useReportsPresentation';
import { downloadBlobFile } from '@/utils/fileDownload';
import {
  REPORTS_SALES_FALLBACK_REPORT_CODE,
  SALES_PROFITABILITY_REPORT_CODE,
  REPORTS_TABLE_EXPORTABLE_CODES,
  REPORTS_SALES_TEXT,
  requireReportsSalesTextValue,
  normalizeReportsSalesReportCode,
  normalizeReportsMetricCards,
  normalizeReportsTablePayload,
  buildReportsSalesExportFileName,
  buildReportsSalesHeaderActions,
  buildReportsSalesProfitabilityTab,
  normalizeReportsSalesTabs,
} from '@/composables/reports/sales/useReportsSalesPresentation';
import {
  SALES_PROFITABILITY_CHART_DEFAULT_METRIC,
  buildSalesProfitabilityKpis,
  normalizeSalesProfitabilityChartPayload,
  normalizeSalesProfitabilityPivot,
  normalizeSalesProfitabilityView,
  resolveSalesProfitabilityChartVariantOptions,
} from '@/composables/reports/sales/profitability/useReportsSalesProfitabilityPresentation';
import {
  REPORTS_SORT_DIRECTION_NONE,
  hasReportsToolbarNonSortChanges,
  normalizeReportsToolbarFiltersPayload,
  resolveReportsToolbarSortChange,
} from '@/composables/reports/general/toolbar/useReportsFilterToolbarHelpers';

export default {
  name: 'ReportsSalesPage',
  components: {
    ReportsPageShell,
    ReportsPrimaryTabs,
    ReportsKpiSection,
    ReportsFilterToolbar,
    ReportsChartSection,
    ReportsTable,
    ReportsDetailModal,
    ReportsSalesProfitabilityTable,
  },
  data() {
    return {
      chartMode: 'bar',
      activeReportTab: REPORTS_SALES_FALLBACK_REPORT_CODE,
      reportTabs: [],
      headerActions: buildReportsSalesHeaderActions(),
      lastAppliedFilters: null,
      metricsCardsData: [],
      profitabilityMetricsSummary: null,
      chartPointsData: [],
      tableData: {
        headers: [],
        rows: [],
        period_totals: [],
        grand_total: 0,
      },
      profitabilityTableRows: [],
      profitabilityTableSummary: null,
      profitabilityTablePivotColumns: [],
      detailModal: {
        show: false,
        loading: false,
        title: '',
        contextLine: '',
        subtitle: '',
        columns: [],
        rows: [],
        grandTotal: 0,
      },
      salesProfitabilityFilters: {
        view: 'subtotal',
        pivot: 'negocio',
      },
      salesProfitabilityChartData: {
        variantOptions: resolveSalesProfitabilityChartVariantOptions(REPORTS_SALES_TEXT),
        variants: {},
      },
      salesProfitabilityChartMetric: SALES_PROFITABILITY_CHART_DEFAULT_METRIC,
      activeSortDirection: REPORTS_SORT_DIRECTION_NONE,
    };
  },
  async mounted() {
    await this.loadReportTabs();
    await this.refreshReportData();
  },
  computed: {
    currentTabConfig() {
      const match = this.reportTabs.find((tab) => tab.key === this.activeReportTab);
      return match || this.reportTabs[0] || null;
    },
    currentHeaderTitle() {
      return String(this.currentTabConfig?.headerTitle || '').trim() || 'Reportes de negocios';
    },
    currentHeaderSubtitle() {
      const tabDescription = String(this.currentTabConfig?.description || '').trim();
      if (tabDescription !== '') {
        return tabDescription;
      }

      const tabLabel = String(this.currentTabConfig?.label || '').trim();
      return tabLabel !== '' ? `Reporte de ${tabLabel}` : 'Reporte de negocios';
    },
    kpiCards() {
      return this.metricsCardsData;
    },
    chartPoints() {
      if (this.isSalesProfitabilityTab) {
        return [];
      }

      return this.chartPointsData;
    },
    chartCategories() {
      if (!this.isSalesProfitabilityTab) {
        return [];
      }

      return this.activeSalesProfitabilityChart.categories;
    },
    chartSeriesData() {
      if (!this.isSalesProfitabilityTab) {
        return [];
      }

      return this.activeSalesProfitabilityChart.series;
    },
    salesProfitabilityChartVariantOptions() {
      const source = Array.isArray(this.salesProfitabilityChartData?.variantOptions)
        ? this.salesProfitabilityChartData.variantOptions
        : [];

      return source.length > 0
        ? source
        : resolveSalesProfitabilityChartVariantOptions(REPORTS_SALES_TEXT);
    },
    activeSalesProfitabilityChart() {
      const variants = this.salesProfitabilityChartData?.variants
        && typeof this.salesProfitabilityChartData.variants === 'object'
        ? this.salesProfitabilityChartData.variants
        : {};
      const activeKey = String(this.salesProfitabilityChartMetric || '').trim();
      const fallbackKey = String(this.salesProfitabilityChartVariantOptions[0]?.value || '').trim();
      const activeVariant = variants[activeKey] || variants[fallbackKey] || {};

      return {
        categories: Array.isArray(activeVariant?.categories) ? activeVariant.categories : [],
        series: Array.isArray(activeVariant?.series) ? activeVariant.series : [],
      };
    },
    isSalesProfitabilityTab() {
      return this.activeReportTab === SALES_PROFITABILITY_REPORT_CODE;
    },
  },
  methods: {
    resetSalesProfitabilityChartData() {
      this.salesProfitabilityChartData = {
        variantOptions: resolveSalesProfitabilityChartVariantOptions(REPORTS_SALES_TEXT),
        variants: {},
      };
    },
    buildRequestParams() {
      const filters = this.lastAppliedFilters && typeof this.lastAppliedFilters === 'object'
        ? this.lastAppliedFilters
        : {};
      const requestFilters = { ...filters };
      delete requestFilters.sort_direction;

      return {
        ...requestFilters,
        report: normalizeReportsSalesReportCode(this.activeReportTab),
      };
    },
    handleToolbarFiltersChange(payload, meta = {}) {
      const { sortDirection, nextFilters } = resolveReportsToolbarSortChange({
        payload,
        meta,
        currentFilters: this.lastAppliedFilters,
      });

      this.activeSortDirection = sortDirection;
      this.lastAppliedFilters = nextFilters;
    },
    async fetchReportData({ skipLoading = false } = {}) {
      const requestConfig = skipLoading ? { skipLoading: true } : {};
      const response = await reportsSalesService.table(this.buildRequestParams(), requestConfig);
      const payload = extractPayload(response);

      if (this.isSalesProfitabilityTab) {
        this.profitabilityMetricsSummary = payload?.summary && typeof payload.summary === 'object'
          ? payload.summary
          : null;
        this.metricsCardsData = buildSalesProfitabilityKpis(
          this.profitabilityMetricsSummary || {},
          this.salesProfitabilityFilters?.view,
          REPORTS_SALES_TEXT.profitability.kpis
        );
      } else {
        this.profitabilityMetricsSummary = null;
        this.metricsCardsData = normalizeReportsMetricCards(payload?.cards || []);
      }

      this.tableData = normalizeReportsTablePayload(payload?.table || {});

      if (this.isSalesProfitabilityTab) {
        this.profitabilityTableRows = Array.isArray(payload?.table?.rows) ? payload.table.rows : [];
        this.profitabilityTableSummary = payload?.table?.summary && typeof payload.table.summary === 'object'
          ? payload.table.summary
          : null;
        this.profitabilityTablePivotColumns = Array.isArray(payload?.table?.pivot_columns)
          ? payload.table.pivot_columns
          : [];
        this.salesProfitabilityChartData = normalizeSalesProfitabilityChartPayload(
          payload?.chart || {},
          this.salesProfitabilityChartVariantOptions
        );

        const allowedVariantCodes = this.salesProfitabilityChartVariantOptions
          .map((option) => String(option?.value || '').trim())
          .filter((value) => value !== '');
        if (!allowedVariantCodes.includes(this.salesProfitabilityChartMetric)) {
          this.salesProfitabilityChartMetric = allowedVariantCodes[0] || SALES_PROFITABILITY_CHART_DEFAULT_METRIC;
        }

        this.chartPointsData = [];
        return;
      }

      this.profitabilityTableRows = [];
      this.profitabilityTableSummary = null;
      this.profitabilityTablePivotColumns = [];
      this.resetSalesProfitabilityChartData();
      this.chartPointsData = mapReportsChartPoints(payload?.chart || {});
    },
    async refreshReportData({ skipLoading = false } = {}) {
      try {
        await this.fetchReportData({ skipLoading });
      } catch (error) {
        this.metricsCardsData = [];
        this.profitabilityMetricsSummary = null;
        this.profitabilityTableRows = [];
        this.profitabilityTableSummary = null;
        this.profitabilityTablePivotColumns = [];
        this.tableData = normalizeReportsTablePayload({});
        this.chartPointsData = [];
        this.resetSalesProfitabilityChartData();
      }
    },
    async loadReportTabs() {
      const localTabs = [buildReportsSalesProfitabilityTab()];

      try {
        const response = await reportsSalesService.selectors({ report: normalizeReportsSalesReportCode(this.activeReportTab) }, { skipLoading: true });
        const payload = extractPayload(response);
        const backendTabs = normalizeReportsSalesTabs(payload?.reports || payload?.tabs);
        const allTabs = backendTabs.length > 0 ? backendTabs : localTabs;

        if (allTabs.length === 0) {
          this.reportTabs = localTabs;
          this.activeReportTab = normalizeReportsSalesReportCode(localTabs[0]?.key || REPORTS_SALES_FALLBACK_REPORT_CODE);
          return;
        }

        this.reportTabs = allTabs;

        const defaultTab = allTabs.find((tab) => tab?.isDefault);
        if (defaultTab) {
          this.activeReportTab = normalizeReportsSalesReportCode(defaultTab.key);
          return;
        }

        const isActiveTabValid = allTabs.some((tab) => tab.key === this.activeReportTab);
        if (!isActiveTabValid) {
          this.activeReportTab = String(allTabs[0]?.key || REPORTS_SALES_FALLBACK_REPORT_CODE);
        }
      } catch (error) {
        this.reportTabs = localTabs;
        this.activeReportTab = normalizeReportsSalesReportCode(localTabs[0]?.key || REPORTS_SALES_FALLBACK_REPORT_CODE);
      }
    },
    async handleReportTabSelect(tabKey) {
      const nextTab = normalizeReportsSalesReportCode(tabKey);
      if (nextTab === '' || nextTab === this.activeReportTab) {
        return;
      }

      this.closeDetailModal();
      this.activeReportTab = nextTab;
      await this.refreshReportData();
    },
    async onHeaderAction(action) {
      const actionKey = String(action?.key || '').trim().toLowerCase();
      if (!['excel', 'pdf'].includes(actionKey)) {
        return;
      }

      const reportCode = normalizeReportsSalesReportCode(this.activeReportTab);
      if (!REPORTS_TABLE_EXPORTABLE_CODES.includes(reportCode)) {
        this.$toast?.info?.(
          requireReportsSalesTextValue(
            REPORTS_SALES_TEXT.page.toasts.exportUnavailable,
            'reports.sales.page.toasts.exportUnavailable'
          )
        );
        return;
      }

      try {
        const params = this.buildRequestParams();
        const response = actionKey === 'excel'
          ? await reportsSalesService.excel(params)
          : await reportsSalesService.pdf(params);
        const extension = actionKey === 'excel' ? 'xlsx' : 'pdf';
        const mimeType = actionKey === 'excel'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/pdf';
        const fileName = buildReportsSalesExportFileName(reportCode, extension);

        downloadBlobFile(response, fileName, mimeType);

        this.$toast?.success(
          actionKey === 'excel'
            ? requireReportsSalesTextValue(REPORTS_SALES_TEXT.page.toasts.excelSuccess, 'reports.sales.page.toasts.excelSuccess')
            : requireReportsSalesTextValue(REPORTS_SALES_TEXT.page.toasts.pdfSuccess, 'reports.sales.page.toasts.pdfSuccess')
        );
      } catch (error) {
        this.$toast?.error(
          error?.response?.data?.message
            || error?.message
            || (actionKey === 'excel'
              ? requireReportsSalesTextValue(REPORTS_SALES_TEXT.page.toasts.excelError, 'reports.sales.page.toasts.excelError')
              : requireReportsSalesTextValue(REPORTS_SALES_TEXT.page.toasts.pdfError, 'reports.sales.page.toasts.pdfError'))
        );
      }
    },
    handleSalesProfitabilityChartVariantChange(nextVariant) {
      if (!this.isSalesProfitabilityTab) {
        return;
      }

      const normalizedVariant = String(nextVariant || '').trim().toLowerCase();
      const allowed = this.salesProfitabilityChartVariantOptions.map((option) => option.value);
      this.salesProfitabilityChartMetric = allowed.includes(normalizedVariant)
        ? normalizedVariant
        : SALES_PROFITABILITY_CHART_DEFAULT_METRIC;
    },
    async handleApplyFilters(payload) {
      this.closeDetailModal();

      const nextPayload = normalizeReportsToolbarFiltersPayload(payload);

      if (this.isSalesProfitabilityTab) {
        const viewFromToolbar = normalizeSalesProfitabilityView(nextPayload?.values?.[0] || nextPayload?.view);
        const pivotFromToolbar = normalizeSalesProfitabilityPivot(nextPayload?.columns?.[0] || nextPayload?.pivot);

        this.salesProfitabilityFilters = {
          view: viewFromToolbar,
          pivot: pivotFromToolbar,
        };
      }

      const shouldReloadData = hasReportsToolbarNonSortChanges(nextPayload, this.lastAppliedFilters);

      this.lastAppliedFilters = nextPayload;
      this.activeSortDirection = nextPayload.sort_direction;

      if (shouldReloadData) {
        await this.refreshReportData({ skipLoading: false });
      }
    },
    closeDetailModal() {
      this.detailModal = {
        ...this.detailModal,
        show: false,
      };
    },
    buildDetailRequestParams(payload = {}) {
      const requestParams = {
        report: normalizeReportsSalesReportCode(this.activeReportTab),
      };

      if (this.isSalesProfitabilityTab) {
        requestParams.values = [normalizeSalesProfitabilityView(this.salesProfitabilityFilters?.view)];
      }

      if (Array.isArray(payload.element_ids) && payload.element_ids.length > 0) {
        requestParams.element_ids = payload.element_ids;
      }

      const detailSource = String(payload.detail_source || '').trim().toLowerCase();
      if (detailSource !== '') {
        requestParams.detail_source = detailSource;
      }

      const detailMetric = String(payload.detail_metric || '').trim().toLowerCase();
      if (detailMetric !== '') {
        requestParams.detail_metric = detailMetric;
      }

      return requestParams;
    },
    async handleProfitabilityMetricCellClick(payload) {
      await this.handlePeriodCellClick(payload);
    },
    buildDetailContextLine(payload = {}) {
      const primaryPivotName = String(payload?.primary_pivot_name || '').trim();
      const secondaryPivotName = String(payload?.secondary_pivot_name || '').trim();
      const columnLabel = String(payload?.column_label || payload?.metric_label || '').trim();

      return [primaryPivotName, secondaryPivotName, columnLabel]
        .filter((value) => value !== '')
        .join(' - ');
    },
    async handlePeriodCellClick(payload) {
      if (!Array.isArray(payload?.element_ids) || payload.element_ids.length === 0) {
        return;
      }

      const metricLabel = String(payload?.metric_label || '').trim();
      const reportLabel = this.currentTabConfig?.label
        || requireReportsSalesTextValue(REPORTS_SALES_TEXT.detail.defaultReportLabel, 'reports.sales.detail.defaultReportLabel');
      const detailTitlePrefix = requireReportsSalesTextValue(REPORTS_SALES_TEXT.detail.titlePrefix, 'reports.sales.detail.titlePrefix');
      const detailTitle = metricLabel !== ''
        ? `${detailTitlePrefix} ${metricLabel}`
        : `${detailTitlePrefix} ${reportLabel}`;
      const detailContextLine = this.buildDetailContextLine(payload);

      this.detailModal = {
        ...this.detailModal,
        show: true,
        loading: true,
        title: detailTitle,
        contextLine: detailContextLine,
        subtitle: requireReportsSalesTextValue(REPORTS_SALES_TEXT.detail.loadingSubtitle, 'reports.sales.detail.loadingSubtitle'),
        columns: [],
        rows: [],
        grandTotal: 0,
      };

      try {
        const response = await reportsSalesService.detail(
          this.buildDetailRequestParams(payload),
          { skipLoading: true }
        );
        const data = extractPayload(response);
        const detail = data?.detail || {};
        const columns = Array.isArray(detail?.columns) ? detail.columns : [];
        const rows = Array.isArray(detail?.rows) ? detail.rows : [];
        const count = Number(detail?.count || rows.length || 0);

        this.detailModal = {
          ...this.detailModal,
          loading: false,
          title: detailTitle,
          contextLine: detailContextLine,
          subtitle: `${count} ${count === 1
            ? requireReportsSalesTextValue(REPORTS_SALES_TEXT.detail.recordLabelSingular, 'reports.sales.detail.recordLabelSingular')
            : requireReportsSalesTextValue(REPORTS_SALES_TEXT.detail.recordLabelPlural, 'reports.sales.detail.recordLabelPlural')}`,
          columns,
          rows,
          grandTotal: Number(detail?.grand_total ?? 0),
        };
      } catch (error) {
        this.detailModal = {
          ...this.detailModal,
          loading: false,
          contextLine: detailContextLine,
          subtitle: requireReportsSalesTextValue(REPORTS_SALES_TEXT.detail.loadErrorSubtitle, 'reports.sales.detail.loadErrorSubtitle'),
          columns: [],
          rows: [],
          grandTotal: 0,
        };
      }
    },
  },
};
</script>
